import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft } from "lucide-react";

export default function EventDetail() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/events/${id}`);
        setEvent(data);
      } finally { setLoading(false); }
    })();
  }, [id]);

  if (loading) return <div className="space-y-3"><Skeleton className="h-8 w-40" /><Skeleton className="h-40 w-full" /></div>;
  if (!event) return <div className="text-neutral-500">Event not found.</div>;

  const meta = event.metadata || event;

  return (
    <div className="space-y-6" data-testid="event-detail">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-900">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <div>
        <div className="text-xs uppercase tracking-wide text-neutral-500">{event.type}</div>
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mt-1">{event.title}</h1>
        <div className="text-neutral-500 mt-1 text-sm">{event.date}</div>
      </div>
      {event.summary && <p className="text-neutral-800 leading-relaxed max-w-3xl">{event.summary}</p>}

      {Array.isArray(meta?.biomarkers) && meta.biomarkers.length > 0 && (
        <section>
          <h2 className="text-sm uppercase tracking-wide text-neutral-500 mb-2">Biomarkers</h2>
          <div className="bg-white rounded-2xl border border-neutral-200 divide-y divide-neutral-100">
            {meta.biomarkers.map((b, i) => (
              <div key={i} className="flex items-baseline justify-between p-3">
                <div className="font-medium text-neutral-900">{b.name || b.canonical_name}</div>
                <div className="text-sm text-neutral-700">{b.value} {b.unit}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {Array.isArray(meta?.medications) && meta.medications.length > 0 && (
        <section>
          <h2 className="text-sm uppercase tracking-wide text-neutral-500 mb-2">Medications</h2>
          <div className="bg-white rounded-2xl border border-neutral-200 divide-y divide-neutral-100">
            {meta.medications.map((m, i) => (
              <div key={i} className="p-3">
                <div className="font-medium">{m.name}</div>
                {m.dose && <div className="text-sm text-neutral-600">{m.dose}</div>}
              </div>
            ))}
          </div>
        </section>
      )}

      {Array.isArray(meta?.diagnoses) && meta.diagnoses.length > 0 && (
        <section>
          <h2 className="text-sm uppercase tracking-wide text-neutral-500 mb-2">Diagnoses</h2>
          <div className="flex flex-wrap gap-2">
            {meta.diagnoses.map((d, i) => (
              <span key={i} className="text-xs px-3 py-1.5 rounded-full bg-white border border-neutral-200">
                {d.name || d}
              </span>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
